// routes/_authenticated/companies/$cnpj/itr-evolution.tsx — ITR evolution
// page for one CNPJ. Reads `year` from search params (default 2025) and
// renders the feature's main page component.
import { toDigitsCnpj } from "@shared/utils/cnpj";
import { createFileRoute, redirect } from "@tanstack/react-router";

import { ItrEvolutionPage } from "@/features/itr-evolution";

type ParsedSearch = { year: number };

const EMPTY_SEARCH: ParsedSearch = { year: 2025 };

function parseSearch(input: Record<string, unknown>): ParsedSearch {
  const raw = input.year;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) && n > 1900 ? { year: n } : EMPTY_SEARCH;
}

export const Route = createFileRoute("/_authenticated/companies/$cnpj/itr-evolution")({
  validateSearch: (input: Record<string, unknown>): ParsedSearch => parseSearch(input),
  // ponytail: same URL-canonicalization trick as the parent company route.
  // `beforeLoad` runs before any rendering; if the path param carries a
  // non-canonical CNPJ (formatted, quoted, anything with non-digits besides
  // the 14 digits), we re-emit the cleaned path with `replace: true`.
  beforeLoad: ({ params, search }) => {
    const raw = params.cnpj;
    const clean = toDigitsCnpj(raw);
    if (clean && clean !== raw) {
      // ponytail: rewrite only the path; preserve the search (year filter)
      // unchanged so a redirect from /companies/27.093.558/0001-15/itr-evolution?year=2025
      // lands the user on /companies/27093558000115/itr-evolution?year=2025.
      throw redirect({
        to: "/companies/$cnpj/itr-evolution",
        params: { cnpj: clean },
        search,
        replace: true,
      });
    }
  },
  component: ItrEvolutionRoute,
});

function ItrEvolutionRoute() {
  const { cnpj: rawCnpj } = Route.useParams();
  const search = Route.useSearch();
  const cnpj = decodeURIComponent(rawCnpj);
  return <ItrEvolutionPage cnpj={cnpj} year={search.year} />;
}
